import { SUPPORTED_LANGUAGES } from "../config";
import { BASE } from "./constants";

export interface Country {
  code: string;
  [key: string]: string;
}

export function getCountryName(country: Country, lang: string): string {
  return country[`name_${lang}`] ?? country.name_en;
}

const SUPPORTED_LANGS = new Set(Object.keys(SUPPORTED_LANGUAGES));

const getCountries = async (lang: string): Promise<Country[]> => {
  const safeLang = SUPPORTED_LANGS.has(lang) ? lang : "en";
  const res = await fetch(`${BASE}/countries/?lang=${safeLang}`);
  if (res.ok) {
    return res.json();
  }
  throw new Error("Failed to fetch countries");
};

export interface CurrentCountry {
  code: string | null;
}

const currentCountryCache = new Map<string, CurrentCountry>();
const currentCountryRequests = new Map<string, Promise<CurrentCountry>>();

const getCachedCurrentCountry = (lang: string): CurrentCountry | undefined => {
  const safeLang = SUPPORTED_LANGS.has(lang) ? lang : "en";
  return currentCountryCache.get(safeLang);
};

const getCurrentCountry = async (lang: string): Promise<CurrentCountry> => {
  const safeLang = SUPPORTED_LANGS.has(lang) ? lang : "en";
  const cached = currentCountryCache.get(safeLang);
  if (cached !== undefined) return cached;

  const pending = currentCountryRequests.get(safeLang);
  if (pending !== undefined) return pending;

  const request = fetch(`${BASE}/countries/current/${safeLang}`)
    .then(async (res): Promise<CurrentCountry> => {
      if (!res.ok) return { code: null };
      return res.json();
    })
    .then((current) => {
      currentCountryCache.set(safeLang, current);
      return current;
    })
    .finally(() => {
      currentCountryRequests.delete(safeLang);
    });

  currentCountryRequests.set(safeLang, request);
  return request;
};

export { getCachedCurrentCountry, getCountries, getCurrentCountry };
